import dayjs from 'dayjs';
import duration from 'dayjs/plugin/duration';
import relativeTime from 'dayjs/plugin/relativeTime';
import localizedFormat from 'dayjs/plugin/localizedFormat';

import {
  altitudeFromMeters,
  altitudeUnitString,
  distanceFromMeters,
  distanceUnitString,
  speedFromKnots,
  speedUnitString,
  volumeFromLiters,
  volumeUnitString,
} from './converter';
import { prefixString } from './stringUtils';

dayjs.extend(duration);
dayjs.extend(relativeTime);
dayjs.extend(localizedFormat);

export const formatBoolean = (value, t) => (value ? t('sharedYes') : t('sharedNo'));

/** MOD */
export const formatDate = (value, format) => {
  let fmt = format;
  try {
    // window.__TRACCAR_DATE_TIME_FORMAT pode ser definido no LocalizationProvider
    if (!fmt && typeof window !== 'undefined' && window.__TRACCAR_DATE_TIME_FORMAT) {
      fmt = window.__TRACCAR_DATE_TIME_FORMAT;
    }
  } catch {
    // ignora ambientes sem window
  }

  // Normaliza tokens para o padrão Day.js
  if (fmt) {
    fmt = fmt.replace(/yyyy/g, 'YYYY').replace(/\byy\b/g, 'YY').replace(/dd/g, 'DD');
  }

  // ✅ Fix cirúrgico: evita deslocamento UTC em datas puras e meia-noite
  let date;
  if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    // "YYYY-MM-DD" → parse local
    date = dayjs(value, 'YYYY-MM-DD', true);
  } else if (typeof value === 'string' && /T00:00:00(\.000)?Z$/.test(value)) {
    // "YYYY-MM-DDT00:00:00Z" → parse UTC sem deslocar
    date = dayjs.utc(value);
  } else {
    // qualquer outro formato → respeita fuso do usuário
    date = dayjs(value);
  }

  // Sem máscara → exibe conforme locale do navegador
  if (!fmt) {
    try {
      return date.toDate().toLocaleString();
    } catch {
      return date.toISOString();
    }
  }

  return date.format(fmt);
}

export const formatDataByte = (bytes, si = false, dp = 1) => {
  const thresh = si ? 1000 : 1024;

  if (Math.abs(bytes) < thresh) {
    return bytes + ' B';
  }

  const units = si
    ? ['kB', 'MB', 'GB', 'TB', 'PB', 'EB', 'ZB', 'YB']
    : ['KiB', 'MiB', 'GiB', 'TiB', 'PiB', 'EiB', 'ZiB', 'YiB'];
  let u = -1;
  const r = 10 ** dp;

  do {
    bytes /= thresh;
    ++u;
  } while (Math.round(Math.abs(bytes) * r) / r >= thresh && u < units.length - 1);

  return bytes.toFixed(dp) + ' ' + units[u];
}

export const courseFormatter = (value, t) => {
  const courseValues = [t('directionN'), t('directionNE'), t('directionE'), t('directionSE'), t('directionS'), t('directionSW'), t('directionW'), t('directionNW')];
  return courseValues[Math.floor(value / 45)];
};

export const formatNumber = (value, precision = 1) => Number(value.toFixed(precision));

export const formatPercentage = (value) => `${value}%`;

export const formatTemperature = (value) => `${value.toFixed(1)}°C`;

export const formatVoltage = (value, t) => `${value.toFixed(2)} ${t('sharedVoltAbbreviation')}`;

export const formatConsumption = (value, t) => `${value.toFixed(2)} ${t('sharedLiterPerHourAbbreviation')}`;

// Extrai porções de data/hora da máscara global, cobrindo 'H' e 'h' e time-first/date-first
const splitMask = (fmt) => {
  if (!fmt) return { dateOnly: null, timeOnly: null, minutesMask: null, secondsMask: null };

  // normalizar tokens comuns; você já normaliza em formatDate, mas aqui só lemos
  const mask = String(fmt);

  // tenta encontrar qualquer token de hora H/h
  const idxH = mask.search(/[Hh]/);

  let dateOnly = null;
  let timeOnly = null;

  if (idxH >= 0) {
    // há parte de hora na máscara; pode vir antes ou depois da data
    // vamos tentar extrair a "parte de data" removendo o bloco de hora + seus separadores adjacentes
    // remove H/h + :mm + :ss (opcionais) e espaços
    const timePattern = /(\s)?[Hh]{1,2}(?::mm)?(?::ss)?(\s)?/g;
    dateOnly = mask.replace(timePattern, ' ').trim();
    timeOnly = mask.match(/[Hh]{1,2}(?::mm)?(?::ss)?/)?.[0] || null;

    // se a remoção deixou apenas separadores, caia no null
    if (!/[YyMDd]/.test(dateOnly)) dateOnly = null;
  } else {
    // sem hora explícita na máscara → é “só data”
    dateOnly = mask.trim();
  }

  // máscaras convenientes para minutes/seconds (usando o que a máscara global já tem)
  const uses12h = /hh/.test(mask);
  const HH = uses12h ? 'hh' : 'HH';

  const minutesMask = dateOnly ? `${dateOnly} ${HH}:mm`.trim() : `${HH}:mm`;
  const secondsMask = dateOnly ? `${dateOnly} ${HH}:mm:ss`.trim() : `${HH}:mm:ss`;

  return { dateOnly, timeOnly, minutesMask, secondsMask };
};

export const formatTime = (value, format) => {
  if (!value) return '';

  let globalFmt = null;
  try {
    if (typeof window !== 'undefined' && window.__TRACCAR_DATE_TIME_FORMAT) {
      globalFmt = window.__TRACCAR_DATE_TIME_FORMAT;
    }
  } catch { /* ignore */ }

  const toIso = (v) => (typeof v === 'string' ? v : dayjs(v).toISOString());

  // Se veio uma máscara concreta (ex.: 'DD-MM-YYYY HH:mm:ss'), honre-a direto
  if (typeof format === 'string' && /[YMDHms]/.test(format) && !['date','time','minutes','seconds'].includes(format)) {
    return formatDate(toIso(value), format);
  }

  // --- FIX: extrair partes de data/hora de forma robusta (H/h e time-first) ---
  const parts = splitMask(globalFmt); // dateOnly, timeOnly, minutesMask, secondsMask

  switch (format) {
    case 'date':
      if (parts.dateOnly) return formatDate(toIso(value), parts.dateOnly);
      // sem máscara global válida → locale date
      return dayjs(value).toDate().toLocaleDateString();

    case 'time':
      if (parts.timeOnly) return formatDate(toIso(value), parts.timeOnly);
      return dayjs(value).toDate().toLocaleTimeString();

    case 'minutes':
      if (parts.minutesMask) return formatDate(toIso(value), parts.minutesMask);
      return dayjs(value).toDate().toLocaleString(undefined, { hour: '2-digit', minute: '2-digit' });

    case 'seconds':
      if (parts.secondsMask) return formatDate(toIso(value), parts.secondsMask);
      return dayjs(value).toDate().toLocaleString(undefined, { hour: '2-digit', minute: '2-digit', second: '2-digit' });

    default:
      return formatDate(toIso(value));
  }
};

export const formatStatus = (value, t) => t(prefixString('deviceStatus', value));

export const formatAlarm = (value, t) => {
  if (value) {
    return value.split(',')
      .map((alarm) => t(prefixString('alarm', alarm)))
      .join(', ');
  }
  return '';
};

export const formatCourse = (value) => {
  const courseValues = ['\u2191', '\u2197', '\u2192', '\u2198', '\u2193', '\u2199', '\u2190', '\u2196'];
  let normalizedValue = (value + 45 / 2) % 360;
  if (normalizedValue < 0) {
    normalizedValue += 360;
  }
  return courseValues[Math.floor(normalizedValue / 45)];
};

export const formatDistance = (value, unit, t) => `${distanceFromMeters(value, unit).toFixed(2)} ${distanceUnitString(unit, t)}`;

export const formatAltitude = (value, unit, t) => `${altitudeFromMeters(value, unit).toFixed(2)} ${altitudeUnitString(unit, t)}`;

export const formatSpeed = (value, unit, t) => `${speedFromKnots(value, unit).toFixed(2)} ${speedUnitString(unit, t)}`;

export const formatVolume = (value, unit, t) => `${volumeFromLiters(value, unit).toFixed(2)} ${volumeUnitString(unit, t)}`;

export const formatNumericHours = (value, t) => {
  const hours = Math.floor(value / 3600000);
  const minutes = Math.floor((value % 3600000) / 60000);
  return `${hours} ${t('sharedHourAbbreviation')} ${minutes} ${t('sharedMinuteAbbreviation')}`;
};

export const formatCoordinate = (key, value, unit) => {
  let hemisphere;
  let degrees;
  let minutes;
  let seconds;

  if (key === 'latitude') {
    hemisphere = value >= 0 ? 'N' : 'S';
  } else {
    hemisphere = value >= 0 ? 'E' : 'W';
  }

  switch (unit) {
    case 'ddm':
      value = Math.abs(value);
      degrees = Math.floor(value);
      minutes = (value - degrees) * 60;
      return `${degrees}° ${minutes.toFixed(3)}' ${hemisphere}`;
    case 'dms':
      value = Math.abs(value);
      degrees = Math.floor(value);
      minutes = Math.floor((value - degrees) * 60);
      seconds = Math.round((value - degrees - minutes / 60) * 3600);
      return `${degrees}° ${minutes}' ${seconds}" ${hemisphere}`;
    default:
      return `${value.toFixed(5)}°`;
  }
};

export const formatAddress = (position, unit) => {
  if (position.address) {
    return position.address;
  }
  const formattedLatitude = formatCoordinate('latitude', position.latitude, unit);
  const formattedLongitude = formatCoordinate('longitude', position.longitude, unit);
  return `${formattedLatitude}, ${formattedLongitude}`;
};

export const getStatusColor = (status, motion = null) => {
  // Se está online e em movimento, retorna azul (info)
  if (status === 'online' && motion === true) {
    return 'info';
  }
  switch (status) {
    case 'online':
      return 'success';
    case 'offline':
      return 'error';
    case 'unknown':
    default:
      return 'neutral';
  }
};

export const getStatusText = (status, t) => {
  switch (status) {
    case 'online':
      return t('deviceStatusOnline');
    case 'offline':
      return t('deviceStatusOffline');
    case 'motion':
      return t('positionMotion');
    default:
      return t('deviceStatusUnknown');
  }
};

export const getBatteryStatus = (batteryLevel) => {
  if (batteryLevel >= 70) {
    return 'success';
  }
  if (batteryLevel > 30) {
    return 'warning';
  }
  return 'error';
};

export const isSameDate = (d1, d2) => d1.format('YYYY-MM-DD') === d2.format('YYYY-MM-DD');

export const formatNotificationTitle = (t, notification, includeId) => {
  if (notification.description) {
    return notification.description;
  }
  let title = t(prefixString('event', notification.type));
  if (notification.type === 'alarm') {
    const alarmString = notification.attributes.alarms;
    if (alarmString) {
      const alarms = alarmString.split(',');
      if (alarms.length > 1) {
        title += ` (${alarms.length})`;
      } else {
        title += ` ${formatAlarm(alarms[0], t)}`;
      }
    }
  }
  if (includeId) {
    title += ` [${notification.id}]`;
  }
  return title;
};